import {createSlice, type SliceSelectors} from '@reduxjs/toolkit'

import {
    checkFdsStateExistence,
    finishFDS,
    initializeFdsState,
    processFDS,
    restoreFdsState,
    restoreFdsStateAfterAuth,
    startFDS,
} from '@web/actions/processFds'
import {FDSPhase, isFDSPass} from '@web/core/constants'
import {resetFDSProcess, FDSStatus, FdsState, getInitialState} from '@web/slices/fds/slice'

import {MakeSliceOptions, SliceReducers} from '../interfaces'

export default (options?: MakeSliceOptions) =>
    createSlice<FdsState, SliceReducers, 'fds', SliceSelectors<FdsState>>({
        name: 'fds',
        initialState: getInitialState(),
        reducers: {},
        extraReducers: (builder) => {
            builder
                .addCase(startFDS, (state, {payload}) => {
                    const {uuid, level, sessionKey, blockReason, fdsCheckHash = '', sessionCheckHash = ''} = payload
                    state.uuid = uuid
                    state.status = FDSStatus.PENDING
                    state.phase = FDSPhase.STARTED

                    const authentication = (() => {
                        if (isFDSPass(level)) {
                            return {
                                level,
                                sessionKey,
                                fdsCheckHash,
                                sessionCheckHash,
                            }
                        } else {
                            return {
                                level,
                                blockReason,
                                fdsCheckHash,
                                sessionCheckHash,
                            }
                        }
                    })()

                    state.authentication = authentication

                    payload.options && (state.options = payload.options)
                })
                .addCase(processFDS.fulfilled, (state, {payload}) => {
                    state.phase = payload.phase
                    state.status = payload.status
                })
                .addCase(checkFdsStateExistence, (state, {payload}) => {
                    state.phase = payload ? FDSPhase.RESTORING : FDSPhase.INITIALIZING
                })
                .addCase(restoreFdsStateAfterAuth, (state, {payload}) => {
                    return (
                        payload ?? {
                            ...state,
                            // 복구할 상태가 없는 것을 확인했거나 복구가 끝난 상태라면 초기상태인 IDLE을 벗어난 것으로 간주한다
                            phase: FDSPhase.IDLE,
                        }
                    )
                })
                .addCase(processFDS.rejected, (state, {payload}) => {
                    state.status = FDSStatus.REJECTED
                    state.phase = FDSPhase.FINISH
                    state.rejectionReason = payload ?? null
                })
                .addCase(finishFDS, (state, {payload}) => {
                    state.phase = FDSPhase.FINISH
                    state.status = payload.status
                    if (payload.status === FDSStatus.REJECTED) {
                        state.rejectionReason = payload.rejectionReason
                    }
                })
                .addCase(resetFDSProcess, (_) => {
                    return {
                        ...getInitialState(),
                        // reset 된 경우는 복구데이터를 기다릴 필요없으므로 무조건 IDLE 이후의 상태로 리셋된다
                        phase: FDSPhase.IDLE,
                    }
                })
            options?.extraReducers?.(builder)
            builder.addCase(initializeFdsState, (_, {payload}) => {
                return {
                    ...getInitialState(),
                    // IDLE 또는 INACTIVE로 돌아간다
                    phase: payload,
                }
            })
            builder.addCase(restoreFdsState, (_, {payload}) => {
                // 전달받은 state로 모두 덮어쓴다
                return payload
            })
        },
    })
